/**
 * Tissue segmentation special case: collapse Gleason sub-labels to base classes.
 *
 * The production `tissue_seg.zarr` outputs (fine28 taxonomy) mix Gleason-graded
 * variants of epithelial classes into the tissue taxonomy so downstream
 * analysis can run end to end, e.g. `luminal_epithelium_g4` (id 132) next to
 * `luminal_epithelium` (id 2). In the annotation UI those variants make glands
 * appear to flip between classes and clutter the class list, even though Gleason
 * grading is annotated in its own overlay / module.
 *
 * For display and editing of the tissue-segmentation overlay we therefore fold
 * every `<base>_g3 | _g4 | _g5 | _g45` class into `<base>` (case-insensitive
 * suffix; the base class must exist in the same taxonomy). This is deliberate,
 * explicit logic for the tissue-seg domain — it does not rely on the
 * `virdx_attrs.misc.id_space` ranges — and it is applied only where the app
 * knows it is looking at the tissue-segmentation overlay.
 *
 * Runtime-neutral: used by the frontend (viewer `OverlayConfig.labelAliases`),
 * scripts, and tests.
 */

/** `domain` metadata value of the tissue segmentation overlay series. */
export const TISSUE_SEGMENTATION_DOMAIN = "tissue_segmentation"

/** `<base>_g3`, `<base>_g4`, `<base>_g5`, `<base>_g45` (any case). */
const GLEASON_SUFFIX_RE = /^(.+)_g(3|4|5|45)$/i

export interface LabelClassEntry {
  id: number
  name: string
}

/** Region metadata shape the resolver needs (structurally matches the viewer's `RegionMetadata`). */
export interface LabelClassNames {
  names: ReadonlyMap<number, string>
}

export function isTissueSegmentationDomain(domain: string | null | undefined): boolean {
  return domain === TISSUE_SEGMENTATION_DOMAIN
}

/** Base class name for a Gleason sub-label name, or null when it is not one. */
export function gleasonBaseClassName(name: string): string | null {
  const match = GLEASON_SUFFIX_RE.exec(name)
  return match ? match[1]! : null
}

/**
 * Alias map (sub-label id → base class id) for a class list. A sub-label is
 * collapsed only when a class with exactly the base name exists; unmatched
 * names are left alone. Ids 0 (background) are never aliased or targeted.
 */
export function resolveGleasonSubLabelAliases(classes: Iterable<LabelClassEntry>): Map<number, number> {
  const entries = [...classes]
  const idByName = new Map<string, number>()
  for (const { id, name } of entries) {
    if (!Number.isInteger(id) || id <= 0) continue
    if (gleasonBaseClassName(name) === null && !idByName.has(name)) idByName.set(name, id)
  }
  const aliases = new Map<number, number>()
  for (const { id, name } of entries) {
    if (!Number.isInteger(id) || id <= 0) continue
    const base = gleasonBaseClassName(name)
    if (base === null) continue
    const target = idByName.get(base)
    if (target === undefined || target === id) continue
    aliases.set(id, target)
  }
  return aliases
}

/** Viewer-compatible resolver: `RegionMetadata` → alias map. */
export function collapseGleasonSubLabels(regions: LabelClassNames): Map<number, number> {
  return resolveGleasonSubLabelAliases([...regions.names].map(([id, name]) => ({ id, name })))
}

/**
 * The label-alias resolver the app applies to an overlay of the given domain
 * (only tissue segmentation today), or undefined for no aliasing.
 */
export function getLabelAliasResolverForDomain(
  domain: string | null | undefined
): ((regions: LabelClassNames) => Map<number, number>) | undefined {
  return isTissueSegmentationDomain(domain) ? collapseGleasonSubLabels : undefined
}
